import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { groupTypesManagement } from "../../../../../../hooks/api/GroupTypesManagement/GroupTypesManagement";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const Types = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 30;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedNoteType, setSelectedNoteType] = useState([]);
    const [noteTypes, setNoteTypes] = useState();
    const { useGroupTypesListMutation, useDeleteGroupTypesMutation } = groupTypesManagement;
    const [noteTypeList, { isLoading }] = useGroupTypesListMutation();
    const [deleteNoteType] = useDeleteGroupTypesMutation();

    const tabs = [
        { title: t("plans.title"), onClick: () => gotoPlansTab() },
        { title: t("groupTypes.title"), isActive: true },
        { title: t("groups.progress"), onClick: () => gotoProgressTab() },
        { title: t("groups.progressTest"), onClick: () => gotoProgressTestTab() },
    ];

    const gotoPlansTab = () => {
        navigate("/settings/groups/plans");
    };

    const gotoProgressTab = () => {
        navigate("/settings/groups/progress");
    };

    const gotoProgressTestTab = () => {
        navigate("/settings/groups/progress-tests");
    };

    const gotoNew = () => {
        navigate("/settings/groups/types/add");
    };

    const gotoEdit = (groupTypeId) => {
        navigate("/settings/groups/types/edit/"+ groupTypeId);
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedNoteType((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedNoteType?.length; i++) {
                            const noteTypeId = selectedNoteType[i];
                            deleteNoteType(noteTypeId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    noteTypeList(filters).then((result) => {
                                        const noteTypes = result?.data?.records;
                                        setNoteTypes(noteTypes);
                                        setTotalRecords(result?.data?.totalRecords);
                                        setDisableDelete(true);
                                        onSelectionChange(noteTypeId);
                                    });
                                    toast.info(result?.data?.info?.responseMessage);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedNoteType.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedNoteType]);

    useEffect(() => {
        noteTypeList(filters).then((result) => {
            const noteTypes = result?.data?.records;
            setNoteTypes(noteTypes);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, noteTypeList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("groupTypes.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("groupTypes.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("groups.title")} />
                    <Link title={t("groupTypes.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("groupTypes.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Minimalno polaznika")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Maksimalno polaznika")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Ima test")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Ima napredak")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Ima izveštaj")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Ima plan")}</span>
                                        </TableHeadCell>
                                        {/* <TableHeadCell>
                                            <span>{t("groupTypes.color")}</span>
                                        </TableHeadCell> */}
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {noteTypes?.length > 0 &&
                                        noteTypes?.map((noteType, index) => {
                                            return (
                                                <TableRow key={noteType.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(noteType.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={noteType.id} onChange={() => onSelectionChange(noteType.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{noteType.name}</TableDataCell>
                                                    <TableDataCell>{noteType.minStudents}</TableDataCell>
                                                    <TableDataCell>{noteType.maxStudents}</TableDataCell>
                                                    <TableDataCell>{noteType.hasTests ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}</TableDataCell>
                                                    <TableDataCell>{noteType.hasProgress ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}</TableDataCell>
                                                    <TableDataCell>{noteType.hasReport ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}</TableDataCell>
                                                    <TableDataCell>{noteType.hasPlan ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}</TableDataCell>
                                                    {/* <TableDataCell>
                                                        <span style={{ backgroundColor: noteType?.color, padding: "2px 10px", borderRadius: "20%" }}></span>
                                                        &nbsp;
                                                        <span>{noteType?.color}</span>
                                                    </TableDataCell> */}
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Types;
